import { computed, signal, Signal } from '@angular/core';

export interface TypewriterRevealOptions {
  intervalMs?: number;
  base?: number;
  catchUpFactor?: number;
  maxStep?: number;
  prefersReducedMotion?: boolean;
}

const DEFAULT_INTERVAL_MS = 40;
const DEFAULT_BASE = 2;
const DEFAULT_CATCH_UP_FACTOR = 0.1;
const DEFAULT_MAX_STEP = 16;

/**
 * View-only character reveal for a streaming answer. Exposes `revealedText` (a computed
 * substring of the full text) that advances on a timer with an adaptive step so it catches
 * up when far behind, snaps to full when streaming ends, and resets when the target message
 * changes. Reduced motion bypasses the animation entirely. Holds no domain/store state.
 */
export class TypewriterReveal {
  private readonly intervalMs: number;
  private readonly base: number;
  private readonly catchUpFactor: number;
  private readonly maxStep: number;
  private prefersReducedMotion: boolean;

  private readonly _fullText = signal('');
  private readonly _revealed = signal(0);
  readonly revealedText: Signal<string> = computed(() => this._fullText().substring(0, this._revealed()));

  /** True when everything currently buffered has been revealed (idle/waiting rather than typing). */
  readonly caughtUp: Signal<boolean> = computed(() => this._revealed() >= this._fullText().length);

  private activeId: string | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(options: TypewriterRevealOptions = {}) {
    this.intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
    this.base = options.base ?? DEFAULT_BASE;
    this.catchUpFactor = options.catchUpFactor ?? DEFAULT_CATCH_UP_FACTOR;
    this.maxStep = options.maxStep ?? DEFAULT_MAX_STEP;
    this.prefersReducedMotion = options.prefersReducedMotion ?? false;
  }

  setState(activeMessageId: string, fullText: string, isStreaming: boolean): void {
    if (activeMessageId !== this.activeId) {
      this.activeId = activeMessageId;
      this._revealed.set(0);
    }
    this._fullText.set(fullText);

    if (this._revealed() > fullText.length) {
      this._revealed.set(fullText.length);
    }

    if (this.prefersReducedMotion || !isStreaming) {
      this._revealed.set(fullText.length);
      this.stop();
      return;
    }

    if (this._revealed() < fullText.length) {
      this.ensureRunning();
    }
  }

  setPrefersReducedMotion(value: boolean): void {
    this.prefersReducedMotion = value;
    if (value) {
      this._revealed.set(this._fullText().length);
      this.stop();
    }
  }

  destroy(): void {
    this.stop();
  }

  private ensureRunning(): void {
    if (this.timer === null) {
      this.timer = setInterval(() => this.tick(), this.intervalMs);
    }
  }

  private stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private tick(): void {
    const full = this._fullText().length;
    const revealed = this._revealed();
    if (revealed >= full) {
      this.stop();
      return;
    }
    const remaining = full - revealed;
    const step = Math.min(remaining, this.maxStep, this.base + Math.ceil(remaining * this.catchUpFactor));
    this._revealed.set(revealed + step);
  }
}
